/**
 * Idioma do OIL TYCOON (só dentro do jogo).
 *
 * A tradução é aplicada nos nós de texto já renderizados: guardamos o texto
 * original de cada nó e trocamos pelo idioma escolhido. Assim o jogo inteiro
 * (telas, botões, avisos e as respostas do servidor) muda de idioma sem
 * reescrever componente nenhum, e em português nada acontece.
 */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { OIL_DICT, OIL_PATTERNS } from './oilDict';

export const OIL_LOCALES = [
  { code: 'pt', label: 'Português' },
  { code: 'zh', label: '繁體中文' },
  { code: 'en', label: 'English' },
];

const OilLocaleContext = createContext({ locale: 'pt', setLocale: () => {}, canChange: false });
export const useOilLocale = () => useContext(OilLocaleContext);

const translateText = (raw, locale) => {
  const key = raw.replace(/\s+/g, ' ').trim();
  if (!key) return null;
  const hit = OIL_DICT[key];
  if (hit && hit[locale] != null) return hit[locale];
  for (const [re, out] of OIL_PATTERNS) {
    const m = key.match(re);
    if (m && out[locale] != null) {
      return out[locale].replace(/\$(\d)/g, (_, i) => m[Number(i)] ?? '');
    }
  }
  if (process.env.NODE_ENV !== 'production' && /[A-Za-zÀ-ÿ]{2}/.test(key)) {
    if (!window.__oilMissing) window.__oilMissing = new Set();
    window.__oilMissing.add(key);
  }
  return null;
};

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA']);

export const OilLocaleProvider = ({ children, locale, setLocale, canChange }) => {
  const value = useMemo(() => ({ locale, setLocale, canChange }), [locale, setLocale, canChange]);
  return <OilLocaleContext.Provider value={value}>{children}</OilLocaleContext.Provider>;
};

/** Traduz (e retraduz ao trocar de idioma) tudo dentro de `rootRef`. */
export const useOilAutoTranslate = (rootRef, locale) => {
  const originals = useRef(new WeakMap());

  useEffect(() => {
    // Diálogos do jogo (regras, escudos, procurados, ficha de serviço...) são
    // renderizados em portal no body, então observamos o documento inteiro.
    const root = document.body;
    if (!root) return undefined;
    const store = originals.current;

    const applyNode = (node) => {
      if (!node || node.nodeType !== Node.TEXT_NODE) return;
      if (node.parentElement && SKIP_TAGS.has(node.parentElement.tagName)) return;
      if (!store.has(node)) {
        if (!/[A-Za-zÀ-ÿ]|º/.test(node.nodeValue || '')) return;
        store.set(node, node.nodeValue);
      }
      const original = store.get(node);
      if (locale === 'pt') {
        if (node.nodeValue !== original) node.nodeValue = original;
        return;
      }
      const translated = translateText(original, locale);
      const next = translated === null ? original
        : original.replace(original.trim(), translated);
      if (node.nodeValue !== next) node.nodeValue = next;
    };

    const applyAttrs = (el) => {
      for (const attr of ['placeholder', 'title', 'aria-label']) {
        const current = el.getAttribute?.(attr);
        if (!current) continue;
        const cacheKey = `__oil_${attr}`;
        if (!el[cacheKey]) el[cacheKey] = current;
        const original = el[cacheKey];
        const translated = locale === 'pt' ? original : translateText(original, locale) || original;
        if (current !== translated) el.setAttribute(attr, translated);
      }
    };

    const walk = (target) => {
      if (target.nodeType === Node.TEXT_NODE) { applyNode(target); return; }
      if (target.nodeType !== Node.ELEMENT_NODE) return;
      applyAttrs(target);
      const it = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
      let n = it.nextNode();
      while (n) { applyNode(n); n = it.nextNode(); }
      target.querySelectorAll?.('[placeholder],[title],[aria-label]').forEach(applyAttrs);
    };

    walk(root);
    const obs = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === 'characterData') applyNode(m.target);
        m.addedNodes?.forEach(walk);
        if (m.type === 'attributes' && m.target.nodeType === Node.ELEMENT_NODE) applyAttrs(m.target);
      }
    });
    obs.observe(root, {
      childList: true, subtree: true, characterData: true,
      attributes: true, attributeFilter: ['placeholder', 'title', 'aria-label'],
    });
    return () => obs.disconnect();
  }, [rootRef, locale]);
};

/** Traduz um texto solto (para textos que não passam pelo DOM do jogo). */
export const useOilText = () => {
  const { locale } = useOilLocale();
  const [, force] = useState(0);
  useEffect(() => force((v) => v + 1), [locale]);
  return (text) => (locale === 'pt' ? text : (translateText(text || '', locale) || text));
};
